import React, { createContext, useEffect, useState } from "react";
import "./App.css";
import { observer } from "mobx-react-lite";
import AppRouter from "./routes/AppRouter";
import { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import { useCloud } from "./hooks/useCloud";
import { useStore } from "./hooks/useStore";
import useOrder from "./hooks/useOrder";
import store from "./store/index";
import { openDB } from "idb";
import { messaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import { playRing } from "./Component/PlaySound";
import { fetchDataAndHandleOrders } from "./service_zomato";
import {
  transformDigitalOrderItems,
  mapLiveOrderObjectToDashboardObject,
} from "./utils/common";
import { Loading } from "./Component/Loading";
const DB_NAME = "orderDB";
const IN_PROCESS_ORDERS_STORE_NAME = "InProcessOrders";
const COMPLETE_ORDERS_STORE_NAME = "completedOrders";
const ORDER_HISTORY_STORE_NAME = "orderHistory";
const DB_VERSION = 1;
let pollingID = null;
let newDigitalOrderAlert = null;

function App() {
  useStore();
  const { liveOrders } = useCloud();
  const { updateOrder, orderExists, saveOrder } = useOrder();

  const [digitalOrderReceived, setDigitalOrderReceived] = React.useState(false);
  const [partnerOrderReceived, setPartnerOrderReceived] = React.useState(false);

  // INIT LOCAL DB
  useEffect(() => {
    const initDB = async () => {
      const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(IN_PROCESS_ORDERS_STORE_NAME)) {
            db.createObjectStore(IN_PROCESS_ORDERS_STORE_NAME, {
              keyPath: "tableVPA",
            });
          }
          if (!db.objectStoreNames.contains(COMPLETE_ORDERS_STORE_NAME)) {
            db.createObjectStore(COMPLETE_ORDERS_STORE_NAME, {
              keyPath: "billNo",
            });
          }
          if (!db.objectStoreNames.contains(ORDER_HISTORY_STORE_NAME)) {
            db.createObjectStore(ORDER_HISTORY_STORE_NAME, {
              keyPath: "billNo",
            });
          }
          if (!db.objectStoreNames.contains("newOrder")) {
            db.createObjectStore("newOrder", {
              keyPath: "order",
            });
          }
        },
      });
      store.setDBInstance(db);
    };

    initDB();
  }, []);

  // DIGITAL ORDER NOTIFICATION
  useEffect(() => {
    const unsubscribe = onMessage(messaging, async (payload) => {
      handleNewOrder();
    });

    return () => unsubscribe();
  }, []);

  // SWIGGY/ZOMATO CHECKUP
  useEffect(() => {
    const interval = setInterval(async () => {
      const count = await fetchDataAndHandleOrders();
      if (count > 0) {
        setPartnerOrderReceived(true);
      }
    }, 20000);
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (pollingID) clearInterval(pollingID);
    if (!store.isLoggedIn) return;
    pollingID = setInterval(async () => {
      const { status, data, billSettlementIds } = await liveOrders();
      if (status === "success") {
        const runningTableVPA = [];
        let digitalOrderCount = 0;
        await Promise.all(
          data
            .map(mapLiveOrderObjectToDashboardObject)
            .map(async (order) => {
              order.items = order.items.map((item) => {
                return {
                  ...item,
                };
              });
              const isExists = await orderExists(order.tableVPA);
              runningTableVPA.push(order.tableVPA);
              if (isExists) updateOrder({ ...order });
              else saveOrder({ ...order });
              window.dispatchEvent(new CustomEvent("liveOrder"));
              if (order.status === "RequestKOT") {
                digitalOrderCount += 1;
                handleNewOrder();
              }
            })
        );

        if (digitalOrderCount === 0) {
          clearTimeout(newDigitalOrderAlert);
          newDigitalOrderAlert = null;
        }

        if (data.length == 0) {
          window.dispatchEvent(new CustomEvent("liveOrder"));
        }

        const db = store.dbInstance;
        if (db) {
          const orders = await db.getAll(IN_PROCESS_ORDERS_STORE_NAME);
          const noExistingOrders = orders.filter(
            (order) => !runningTableVPA.includes(order.tableVPA)
          );
          const settledOrders = orders.filter((order) =>
            billSettlementIds.includes(order.orderID)
          );
          console.log(noExistingOrders);
          const completedVPA = [...settledOrders, ...noExistingOrders].map(
            (order) => order.tableVPA
          );
          completedVPA.forEach((vpa) => {
            db.delete(IN_PROCESS_ORDERS_STORE_NAME, vpa);
          });
        }
      }
    }, 2000);

    return () => {
      clearInterval(pollingID);
    };
  }, [store.isLoggedIn]);

  useEffect(() => {
    window.addEventListener(
      "DIGITAL_ORDER_RECEIVED",
      handleNewDigitalOrderReceived
    );
    return window.removeEventListener(
      "DIGITAL_ORDER_RECEIVED",
      handleNewDigitalOrderReceived
    );
  });

  const handleNewDigitalOrderReceived = async () => {
    setDigitalOrderReceived(true);
  };

  const handleNewOrder = async () => {
    try {
      if (newDigitalOrderAlert) {
        clearTimeout(newDigitalOrderAlert);
      } else {
        setDigitalOrderReceived(true);
        await playRing();
      }

      newDigitalOrderAlert = setTimeout(async () => {
        setDigitalOrderReceived(true);
        await playRing();
      }, 120000);
    } catch (e) {
      console.log(e);
    }
  };

  if (store.appBooting) {
    return (
      <div className="w-screen h-screen flex justify-center items-center flex gap-2 flex-col">
        <Loading type="dots" size="lg" color="success" />{" "}
        <p className="text-xl font-bold">QRapid Booting up</p>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen">
      {/* DIGITAL ORDER */}
      {digitalOrderReceived && (
        <div
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          style={{ zIndex: 501 }}
          onClick={() => setDigitalOrderReceived(false)}
        >
          <div class="bg-white rounded-lg shadow-lg p-6 w-96 text-center relative">
            <div class="absolute top-[10px] left-1/2 transform -translate-x-1/2 text-3xl text-green-500">
              <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="text-xl font-semibold flashing text-gray-800 mt-6">
              Digital Order Received
            </div>
          </div>
        </div>
      )}
      {partnerOrderReceived && (
        // <Link to="/order-parcel" onClick={() => setPartnerOrderReceived(false)}>
        <div
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          style={{ zIndex: 501 }}
          onClick={() => setPartnerOrderReceived(false)}
        >
          <div class="bg-white rounded-lg shadow-lg p-6 w-96 text-center relative">
            <div class="absolute top-[10px] left-1/2 transform -translate-x-1/2 text-3xl text-green-500">
              <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="text-xl font-semibold flashing text-gray-800 mt-6">
              Partner Order Received
            </div>
          </div>
        </div>
        // </Link>
      )}
      <Toaster position="top-right" toastOptions={{ duration: 1000 }} />
      {/* HEADER */}
      {/* SIDEBAR */}

      <AppRouter />
    </div>
  );
}

export default observer(App);
